import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchFun } from '../../../../utils/fetchFun';
import { fetchJournalReportOnce } from '../../../../utils/fetchJournalReportOnce';
// Reused as-is: generic QB Journal Report row parsing, statement-agnostic (no CFS
// business logic in these two - just grouping raw rows into per-entry debit/credit pairs).
import { getUniqeRowsHandler, uniqueRowsFormatter } from '../../../../pages/cfs/utils/helperFunctions';
import { buildSesLibraryIndex, buildAccountRowIndex, generateAndSaveSesStatement } from '../../../../pages/ses/utils/generateStatement';


// export const fetchStockHoldersEquity = createAsyncThunk(
//     'user/fetchStockHoldersEquity',
//     async (userId) => {
//       try{
//         const myHeaders = new Headers();
//         myHeaders.append("Content-Type", "application/json");

//         const requestOptions = {
//           method: "GET",
//           headers: myHeaders,
//           redirect: "follow"
//         };
    
//         const responseRaw = await fetch(`${process.env.REACT_APP_API_KEY}/api/v1/equityholder-statement?userId=${userId}`, requestOptions);
//         if(responseRaw.ok){
//           const response = await responseRaw.json();
//           console.log("response", response)
//             return response?.data;
//         }
//       } catch (error) {
//         console.log("Error - /equityholder-statement", error)
//       }
//     }
// );

export const fetchStockHoldersEquity = createAsyncThunk(
    'user/fetchStockHoldersEquity',
    async ({user}) => {
      try{
        // console.log('UserSession', user);
        const sesData = await fetchFun(`/api/v1/equityholder-statement?userId=${user?._id}`);
        if(sesData?.success) {
          return sesData.data
        }
      } catch (error) {
        console.log("Error - /equityholder-statement", error)
      }
    }
);

// Raw journal entries for SES-WP - reuses the SAME shared, statement-agnostic endpoint CFS-WP
// uses (/api/getJournalReport), so no new backend route was needed for this.
export const fetchSesRawJournalData = createAsyncThunk(
    'user/fetchSesRawJournalData',
    async ({ user }) => {
        try {
            const rjData = await fetchJournalReportOnce(user?._id);
            if (rjData?.success) {
                let periods = {
                    periodOne: null,
                    periodTwo: null
                };
                const p1data = !!rjData?.data?.periodOneData?.journalReport?.qbData ? rjData.data.periodOneData?.journalReport?.qbData.Rows.Row.filter(row => !!row?.ColData && !!row?.ColData?.[1].id) : null;
                const p2data = !!rjData?.data?.periodTwoData?.journalReport?.qbData ? rjData.data.periodTwoData?.journalReport?.qbData.Rows.Row.filter(row => !!row?.ColData && !!row?.ColData?.[1].id) : null;
                if (p1data) {
                    periods.periodOne = uniqueRowsFormatter(getUniqeRowsHandler(p1data)) || null;
                }
                if (p2data) {
                    periods.periodTwo = uniqueRowsFormatter(getUniqeRowsHandler(p2data)) || null;
                }
                return periods;
            }
        } catch (error) {
            console.log("Error - /getJournalReport (SES)", error)
        }
    }
);

// SES Grouping Library - all rows (global + CustomerAdmin overrides merged by backend).
export const fetchSesLibraries = createAsyncThunk(
    'user/fetchSesLibraries',
    async () => {
        try {
            const response = await fetchFun(`/api/v1/equityholder-statement/getSesGrpLibraries`);
            if (response?.success) {
                return response.data;
            }
        } catch (error) {
            console.log("Error - /getSesGrpLibraries", error)
        }
    }
);

// Previously-saved manual M-M splits (SESSplitMultiMultiModal) - mirrors CFS's
// fetchMMMSplitSavedEntries. Backend route already existed (getSesMMMSplitEntries) but was
// never called from the frontend until the M-M split UI was built.
export const fetchSesMMMSplitEntries = createAsyncThunk(
    'user/fetchSesMMMSplitEntries',
    async ({ user }) => {
        try {
            const response = await fetchFun(`/api/v1/equityholder-statement/MMSplitEntries/${user?._id}`);
            if (response?.success) {
                return response.data;
            }
        } catch (error) {
            console.log("Error - /equityholder-statement/MMSplitEntries", error)
        }
    }
);

// The generated SES Statement (Phase 7 output) - Caption x Column amounts per period,
// computed from the Workpaper's library-classified entries + beginning balances.
export const fetchSesStatement = createAsyncThunk(
    'user/fetchSesStatement',
    async ({ user }) => {
        try {
            const response = await fetchFun(`/api/v1/equityholder-statement/statement/${user?._id}`);
            if (response?.success) {
                return response.data;
            }
        } catch (error) {
            console.log("Error - /equityholder-statement/statement", error)
        }
    }
);

// Per-column (Common stock / APIC / Retained earnings) beginning balances - needed to turn
// the Workpaper's classified net-change amounts into a full statement (beginning + change =
// ending), same role CFS's "Cash and cash equivalents" beginning balance plays for CFS.
export const fetchSesBeginningBalances = createAsyncThunk(
    'user/fetchSesBeginningBalances',
    async ({ user }) => {
        try {
            const response = await fetchFun(`/api/v1/equityholder-statement/beginningBalances/${user?._id}`);
            if (response?.success) {
                return response.data;
            }
        } catch (error) {
            console.log("Error - /equityholder-statement/beginningBalances", error)
        }
    }
);

// SES's equivalent of CFS's regenerateCfsReports - but since SES classification happens
// entirely client-side (no backend classification engine, see sesWp.controller.js's own
// comments), "regenerate" means re-fetching the three inputs (journal data, library,
// beginning balances) and recomputing + saving the statement in the browser, not a single
// backend POST. Reuses the same fetch thunks (and their reducers) so the redux cache stays
// consistent with whatever this thunk just computed.
export const regenerateSesReports = createAsyncThunk(
    'user/regenerateSesReports',
    async ({ user, formDetail }, { dispatch, getState }) => {
        try {
            const [sesJournalData, sesLibrary, sesBeginningBalances, mmmSplitEntries] = await Promise.all([
                dispatch(fetchSesRawJournalData({ user })).unwrap(),
                dispatch(fetchSesLibraries()).unwrap(),
                dispatch(fetchSesBeginningBalances({ user })).unwrap(),
                dispatch(fetchSesMMMSplitEntries({ user })).unwrap(),
            ]);

            const sesLibraryIndex = buildSesLibraryIndex(sesLibrary);
            // Already fetched centrally by DefaultLayout - Net Income for the statement's
            // "Net income" row, and the TB-GRP groupings for the Level-2 matching fallback
            // (spec 1.03's "Step-wise Matching sequence"), both come from here, not a fresh fetch.
            const state = getState();
            const incomeStatementData = state.incomeStatement.data;
            const groupings = state.groupingSlice.groupings;
            const accountRowIndexes = {
                periodOne: buildAccountRowIndex(groupings?.existingGroupingPeriodOne?.periodgroupedData),
                periodTwo: buildAccountRowIndex(groupings?.existingGroupingPeriodTwo?.periodgroupedData),
            };

            await Promise.all([
                generateAndSaveSesStatement({
                    periodKey: "periodOne", periodType: formDetail?.period1,
                    sesJournalData, sesLibraryIndex, accountRowIndexes, sesBeginningBalances, incomeStatementData, mmmSplitEntries,
                    clientId: formDetail?.client, customerId: formDetail?.customer, userId: user?._id,
                }),
                generateAndSaveSesStatement({
                    periodKey: "periodTwo", periodType: formDetail?.period2,
                    sesJournalData, sesLibraryIndex, accountRowIndexes, sesBeginningBalances, incomeStatementData, mmmSplitEntries,
                    clientId: formDetail?.client, customerId: formDetail?.customer, userId: user?._id,
                }),
            ]);

            return dispatch(fetchSesStatement({ user })).unwrap();
        } catch (error) {
            console.log("Error - regenerateSesReports", error)
        }
    }
);